// Living Profile aggregator: streaks, meditation, badges, saved teachings,
// favorite music, purchases, reading progress, journal, consciousness level.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function computeStreak(dates: string[]): { current: number; longest: number } {
  if (!dates.length) return { current: 0, longest: 0 };
  const days = Array.from(new Set(dates.map((d) => new Date(d).toISOString().slice(0, 10)))).sort();
  let longest = 1;
  let run = 1;
  for (let i = 1; i < days.length; i++) {
    const prev = new Date(days[i - 1] + "T00:00:00Z").getTime();
    const cur = new Date(days[i] + "T00:00:00Z").getTime();
    if ((cur - prev) / 86400000 === 1) {
      run++;
      longest = Math.max(longest, run);
    } else {
      run = 1;
    }
  }
  // current streak ending today or yesterday
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const last = days[days.length - 1];
  if (last !== today && last !== yesterday) return { current: 0, longest };
  let current = 1;
  for (let i = days.length - 2; i >= 0; i--) {
    const prev = new Date(days[i] + "T00:00:00Z").getTime();
    const next = new Date(days[i + 1] + "T00:00:00Z").getTime();
    if ((next - prev) / 86400000 === 1) current++;
    else break;
  }
  return { current, longest };
}

// Consciousness Level: derived from cumulative engagement.
// Level = floor(sqrt(activations*2 + meditationMin/10 + badges*5 + journal*2 + reading%*0.5))
function computeLevel(input: {
  activations: number;
  meditationMinutes: number;
  badges: number;
  journal: number;
  readingPercentTotal: number;
  gate: number;
}) {
  const raw =
    input.activations * 2 +
    input.meditationMinutes / 10 +
    input.badges * 5 +
    input.journal * 2 +
    input.readingPercentTotal * 0.5 +
    input.gate * 3;
  const level = Math.max(1, Math.floor(Math.sqrt(raw)));
  const nextThreshold = (level + 1) * (level + 1);
  const currentThreshold = level * level;
  const progress = Math.min(1, Math.max(0, (raw - currentThreshold) / (nextThreshold - currentThreshold)));
  const titles = [
    "Seeker", "Initiate", "Student", "Practitioner", "Adept",
    "Guardian", "Illuminated", "Sovereign", "Oracle", "Master",
    "Sage", "Luminary", "Ascended",
  ];
  const title = titles[Math.min(level - 1, titles.length - 1)];
  return { level, title, progress, raw: Math.round(raw) };
}

export const getLivingProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const [
      profileRes,
      completionsRes,
      practiceRes,
      badgesRes,
      favoritesRes,
      ordersRes,
      entitlementsRes,
      readingRes,
      journalRes,
    ] = await Promise.all([
      supabase.from("profiles").select("id, display_name, avatar_url, bio, current_gate, created_at").eq("id", userId).maybeSingle(),
      supabase.from("activation_completions").select("activation_slug, completed_at").eq("user_id", userId).order("completed_at", { ascending: false }).limit(500),
      supabase.from("practice_logs").select("kind, minutes, logged_on, note, created_at").eq("user_id", userId).order("logged_on", { ascending: false }).limit(200),
      supabase.from("user_badges").select("badge_id, awarded_at, badges(slug, title, description, icon)").eq("user_id", userId).order("awarded_at", { ascending: false }),
      supabase.from("user_favorites").select("kind, slug, created_at").eq("user_id", userId).order("created_at", { ascending: false }).limit(200),
      supabase.from("orders").select("id, order_number, product_slug, product_title, status, amount_cents, currency, fulfillment_status, created_at").eq("user_id", userId).order("created_at", { ascending: false }).limit(50),
      supabase.from("product_entitlements").select("product_slug, granted_at, expires_at, revoked_at").eq("user_id", userId),
      supabase.from("reading_progress").select("title_id, percent, seconds_read, last_read_at").eq("user_id", userId).order("last_read_at", { ascending: false }),
      supabase.from("journal_entries").select("id, title, body, gate_n, mood, tags, is_pinned, created_at, updated_at").eq("user_id", userId).order("updated_at", { ascending: false }).limit(20),
    ]);

    const profile = profileRes.data ?? null;
    const completions = completionsRes.data ?? [];
    const practice = practiceRes.data ?? [];
    const badges = (badgesRes.data ?? []).map((b: any) => ({
      awarded_at: b.awarded_at,
      slug: b.badges?.slug ?? null,
      title: b.badges?.title ?? "Badge",
      description: b.badges?.description ?? null,
      icon: b.badges?.icon ?? null,
    }));
    const favorites = favoritesRes.data ?? [];
    const orders = ordersRes.data ?? [];
    const entitlements = (entitlementsRes.data ?? []).filter(
      (e) => !e.revoked_at && (!e.expires_at || new Date(e.expires_at) > new Date()),
    );
    const reading = readingRes.data ?? [];
    const journal = journalRes.data ?? [];

    const meditationMinutes = practice.reduce((s, p) => s + (p.minutes ?? 0), 0);
    const streak = computeStreak(completions.map((c) => c.completed_at as string));
    const readingPercentTotal = reading.reduce((s, r) => s + (Number(r.percent) || 0), 0);

    const consciousness = computeLevel({
      activations: completions.length,
      meditationMinutes,
      badges: badges.length,
      journal: journal.length,
      readingPercentTotal,
      gate: profile?.current_gate ?? 0,
    });

    return {
      profile,
      consciousness,
      stats: {
        activations: completions.length,
        streakCurrent: streak.current,
        streakLongest: streak.longest,
        meditationMinutes,
        meditationSessions: practice.length,
        badges: badges.length,
        favorites: favorites.length,
        orders: orders.length,
        activeEntitlements: entitlements.length,
        booksInProgress: reading.length,
        journalEntries: journal.length,
      },
      badges,
      favorites: {
        teachings: favorites.filter((f) => ["wisdom", "teaching", "codex", "gate"].includes(f.kind)),
        music: favorites.filter((f) => ["song", "release", "music"].includes(f.kind)),
        books: favorites.filter((f) => ["book", "title"].includes(f.kind)),
        other: favorites.filter((f) => !["wisdom", "teaching", "codex", "gate", "song", "release", "music", "book", "title"].includes(f.kind)),
      },
      orders,
      entitlements,
      reading,
      journal,
      recentCompletions: completions.slice(0, 10),
      recentPractice: practice.slice(0, 10),
    };
  });
